import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Button,
  FormControl,
  FormLabel,
  Chip,
  IconButton,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  Badge,
  Snackbar,
  Grow,
  LinearProgress,
} from '@mui/material';
import useSolarFormStore from '../../store/SolarFormStore';
import {
  Close,
  Edit,
  House,
  LocationOn,
  PermIdentity,
  Phone,
  Warehouse,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  addCustomerDetails,
  addReferral,
  getTokens,
} from '../../services/CustomerService';
import { months } from '../constant';
import RedirectModal from './RedirectModal';
import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded';

function GrowTransition(props) {
  return <Grow {...props} />;
}
const SolarQuoteForm = () => {
  const [propertyType, setPropertyType] = useState('RESIDENTIAL');
  const [pincode, setPincode] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [monthlyBill, setMonthlyBill] = useState('upto1500');
  const [consent, setConsent] = useState(true);
  const [state, setState] = useState('');
  const [maxBill, setMaxBill] = useState(1500);
  const [minBill, setMinBill] = useState(1000);
  const [registeredUserDetails, setRegisteredUserDetails] = useState({});
  const [isAlert, setIsAlert] = useState({ show: false, msg: '' });
  const [whiteListStateModal, setWhiteListStateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const whiteListState = ['Delhi'];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [referralCode, setReferralCode] = useState('');
  const [searchParams] = useSearchParams();
  const r = searchParams.get('r');
  const [referalCode, setReferalCode] = useState(null);
  useEffect(() => {
    if (r) {
      setReferalCode(r);
    }
  }, [r]);

  const billMapping = {
    upto1500: { minbill: 1000, maxbill: 1500 },
    '1500-3000': { minbill: 1500, maxbill: 3000 },
    '3000-10000': { minbill: 3000, maxbill: 10000 },
    '10000above': { minbill: 10000, maxbill: 20000 },
  };
  const getBillRange = (value) => billMapping[value] || null;

  const [errors, setErrors] = useState({
    pincode: '',
    name: '',
    phoneNumber: '',
  });

  // Modified handleChange with real-time validation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Update state first
    if (name === 'propertyType') {
      setPropertyType(value);
    }
    if (type === 'checkbox') {
      setConsent(checked);
    } else {
      if (name === 'monthlyBill') {
        const { minbill, maxbill } = getBillRange(value);
        setMinBill(minbill);
        setMaxBill(maxbill);
        setMonthlyBill(value);
      } else {
        if (name === 'pincode') setPincode(value);
        if (name === 'name') setName(value);
        if (name === 'phoneNumber') setPhoneNumber(value);
      }
    }

    if (name === 'pincode') {
      const pincodeRegex = /^\d{6}$/;
      setErrors((prev) => ({
        ...prev,
        pincode:
          value.length >= 6
            ? !pincodeRegex.test(value)
              ? 'Must be exactly 6 digits'
              : ''
            : value === ''
              ? 'Pincode is required'
              : '',
      }));
    }

    if (name === 'name') {
      setErrors((prev) => ({
        ...prev,
        name: value.trim() ? '' : 'Name is required',
      }));
    }

    if (name === 'phoneNumber') {
      const phoneRegex = /^\d{10}$/;
      setErrors((prev) => ({
        ...prev,
        phoneNumber:
          value.length >= 10 // Check only when 10 digits are typed
            ? !phoneRegex.test(value)
              ? 'Must be exactly 10 digits'
              : ''
            : value === ''
              ? 'Phone number is required'
              : '', // Show required error immediately if empty
      }));
    }
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    const newErrors = {
      name: name.trim() ? '' : 'Name is required',
      pincode: pincode.trim() ? '' : 'Pincode is required',
      phoneNumber:
        phoneNumber === ''
          ? 'Phone number is required'
          : !/^\d{10}$/.test(phoneNumber)
            ? 'Must be exactly 10 digits'
            : '',
    };

    // Update error state
    setErrors(newErrors);

    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some((error) => error !== '');
    if (hasErrors) {
      console.error('Form submission blocked due to errors:', newErrors);
      setIsAlert({
        show: true,
        msg: 'Please fill all the fields correctly',
      });
      return; // Stop submission if there are errors
    }
    const pincodeResult = await validatePincode(pincode);

    if (!pincodeResult.isValid) {
      console.error('Invalid Pincode:', pincodeResult.error);
      setIsAlert({
        show: true,
        msg: 'Invalid Pincode',
      });

      return; // Stop submission if pincode is invalid
    }

    // Set state based on validated pincode
    setState(pincodeResult.state);
    const maxbill = getBillRange(monthlyBill).maxbill;
    const minbill = getBillRange(monthlyBill).minbill;
    const formData = {
      propertyType,
      pincode,
      name,
      phoneNumber,
      maxBill,
      minBill,
      consent,
    };
    const currDate = new Date();
    const newUserRequestParams = {
      fullName: name,
      phoneNumber: phoneNumber,
      propertyDetails: {
        propertyType: propertyType,
        pinCode: pincode,
        shadowFreeArea: 0,
        billAmount: (maxbill + minbill) / 2,
        billMonth:
          months[
            new Date(currDate.setMonth(currDate.getMonth() - 1)).getMonth()
          ],
      },
    };
    console.log('New User Request:', newUserRequestParams);
    try {
      const newUser = await addCustomerDetails(newUserRequestParams);
      if (referalCode) {
        try {
          const referralResponse = await addReferral({
            userId: newUser.id,
            referralCode: referalCode,
          });

          if (referralResponse) {
            setSnackbarMessage('Referral code added successfully!');
            setSnackbarSeverity('success');
          }
        } catch (referralError) {
          console.error('Error adding referral code:', referralError);
          setSnackbarMessage('Error adding referral code.');
          setSnackbarSeverity('error');
        }
        setOpenSnackbar(true);
      }

      setIsAlert({ show: true, msg: 'Account created successfully' });
      const tokens = await getTokens(newUser);
      if (tokens) {
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
      }
      setOpen(true);
    } catch (error) {
      setIsAlert({
        show: true,
        msg: 'Number already used',
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePincode = async (pincode) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );
      const data = await response.json();

      if (data[0].Status === 'Success' && data[0].PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setState(postOffice.State);
        return {
          isValid: true,
          error: '',
          district: postOffice.District,
          state: postOffice.State,
        };
      } else {
        setLoading(false);
        return {
          isValid: false,
          error: 'Invalid pincode',
          district: '',
          state: '',
        };
      }
    } catch (error) {
      setLoading(false);
      return {
        isValid: false,
        error: 'Error validating pincode',
        district: '',
        state: '',
      };
    }
  };
  const handleClose = () => setOpen(false);

  return (
    <Box
      marginBottom={isMobile ? '4rem' : '0'}
      sx={{
        maxWidth: 400,
        mx: 'auto',
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontWeight: '900' }}
        mb={'2rem'}
      >
        Get personalized solar quote
      </Typography>

      {/* Property Type Radio Buttons */}
      <FormControl component='fieldset' sx={{ mb: 2, width: '100%' }}>
        <ToggleButtonGroup
          value={propertyType}
          exclusive
          onChange={(event, newValue) => {
            if (newValue !== null) {
              handleChange({
                target: { name: 'propertyType', value: newValue },
              });
            }
          }}
          size='small'
          sx={{
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #ccc',
            width: '100%',
          }}
        >
          {['RESIDENTIAL', 'COMMERCIAL'].map((type) => (
            <ToggleButton
              key={type}
              value={type}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 0,
                backgroundColor:
                  propertyType === type ? theme.palette.primary.main : 'white',
                color:
                  propertyType === type ? 'white' : theme.palette.primary.main,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': { backgroundColor: 'darkgreen' },
                },
              }}
            >
              {'RESIDENTIAL' === type ? (
                <House sx={{ fontSize: '22px', paddingRight: '5px' }} />
              ) : (
                <CorporateFareRoundedIcon
                  sx={{ fontSize: '22px', paddingRight: '5px' }}
                />
              )}

              {type}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FormControl>
      {/* Pincode */}
      <Stack
        direction={'row'}
        justifyContent='center'
        alignItems='end'
        spacing={2}
      >
        <LocationOn
          fontSize='medium'
          sx={{ color: theme.palette.primary.main }}
        />
        <TextField
          fullWidth
          label='Pincode'
          name='pincode'
          value={pincode}
          onChange={handleChange}
          placeholder='Helps us search local installers'
          variant='standard'
          inputMode='numeric'
          sx={{ mb: 2 }}
          error={!!errors.pincode} // Shows red underline when there's an error
          helperText={errors.pincode} // Shows error message below field
        />
      </Stack>

      {/* Name and Phone Number */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, alignItems: 'end' }}>
        <PermIdentity
          fontSize='medium'
          sx={{ color: theme.palette.primary.main }}
        />
        <TextField
          fullWidth
          name='name'
          value={name}
          onChange={handleChange}
          placeholder='Name'
          variant='standard'
          error={!!errors.name}
          helperText={errors.name}
        />
        <Phone fontSize='medium' sx={{ color: theme.palette.primary.main }} />
        <TextField
          fullWidth
          name='phoneNumber'
          value={phoneNumber}
          onChange={handleChange}
          placeholder='Phone number'
          variant='standard'
          inputMode='numeric'
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
        />
      </Box>

      {/* Average Monthly Bill Radio Buttons */}
      <Typography variant='body2' mb={'1rem'}>
        Average monthly bill
      </Typography>
      <FormControl component='fieldset' sx={{ mb: 2 }}>
        <FormLabel component='legend' sx={{ fontSize: '0.875rem' }}></FormLabel>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { value: 'upto1500', label: 'Upto 1500' },
            { value: '1500-3000', label: '1500-3000' },
            { value: '3000-10000', label: '3000-10000' },
            { value: '10000above', label: '10000 & above' },
          ].map((item) => (
            <Chip
              key={item.value}
              label={item.label}
              variant={monthlyBill === item.value ? 'filled' : 'outlined'}
              onClick={() =>
                handleChange({
                  target: {
                    name: 'monthlyBill',
                    value: item.value,
                    type: 'radio',
                  },
                })
              }
            />
          ))}
        </Box>
      </FormControl>

      {/* Consent Checkbox */}
      <Stack>
        <FormControlLabel
          control={
            <Checkbox
              name='consent'
              checked={consent}
              onChange={handleChange}
            />
          }
          label={<Typography variant='body2'>I own the property.</Typography>}
        />
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontSize: '12px' }}
          mb={'1rem'}
        >
          By clicking above, I authorize VaySolar to reach me about products and
          services even if I am on ‘Do Not Call’ list.
        </Typography>
      </Stack>

      {/* Get Quote Button */}
      <Box position='relative'>
        <Button
          variant='contained'
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
          sx={{ textTransform: 'none', py: 1.5, fontWeight: 'bold' }}
        >
          Get solar quote
        </Button>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
            }}
          />
        )}
      </Box>
      <RedirectModal
        open={open}
        name={name}
        phoneNumber={phoneNumber}
        pincode={pincode}
        onClose={handleClose}
        propertyType={propertyType}
        state={state}
        minBill={minBill}
        maxBill={maxBill}
        consent={consent}
      />
      <Snackbar
        open={isAlert.show}
        TransitionComponent={GrowTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50px !important',
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'white',
            color: 'red',
          },
        }}
        autoHideDuration={1500}
        onClose={() => setIsAlert({ show: false, msg: isAlert.msg })}
        message={isAlert.msg}
        action={
          <IconButton
            size='small'
            aria-label='close'
            color='inherit'
            onClick={() => setIsAlert({ show: false, msg: isAlert.msg })}
          >
            <Close fontSize='small' />
          </IconButton>
        }
      />
    </Box>
  );
};

export default SolarQuoteForm;
